/**
 * Crew Service
 * Frontend service for crew management, meetings, and check-ins
 */

import { telegram } from './telegram';

export interface Crew {
  id: string;
  name: string;
  description: string | null;
  owner_user_id: string;
  owner_username: string | null;
  max_members: number;
  invite_code: string;
  is_active: number;
  created_at: string;
  updated_at: string;
  user_role?: string;
  member_count?: number;
  active_meetings?: number;
  // Lead/timesheet fields
  lead_email?: string | null;
  lead_name?: string | null;
  company_name?: string | null;
  auto_submit_timesheet?: number;
  timesheet_day?: string;
}

export interface CrewMember {
  id: string;
  crew_id: string;
  telegram_user_id: string;
  telegram_username: string | null;
  role: 'owner' | 'admin' | 'member';
  status: 'active' | 'left' | 'kicked';
  last_latitude: number | null;
  last_longitude: number | null;
  last_location_update: string | null;
  joined_at: string;
}

export interface Meeting {
  id: string;
  crew_id: string;
  name: string;
  description: string | null;
  latitude: number;
  longitude: number;
  location_name: string | null;
  scheduled_at: string | null;
  proximity_radius_meters: number;
  status: 'active' | 'scheduled' | 'completed' | 'cancelled';
  created_by: string;
  created_at: string;
  updated_at: string;
  crew_name?: string;
  check_in_count?: number;
}

export interface CheckIn {
  id: string;
  meeting_id: string;
  crew_id: string;
  telegram_user_id: string;
  telegram_username: string | null;
  latitude: number;
  longitude: number;
  distance_meters: number;
  within_proximity: boolean;
  checked_in_at: string;
}

export interface TimesheetSubmission {
  id: string;
  crew_id: string;
  submitted_by: string;
  submitted_by_username: string | null;
  week_start: string;
  week_end: string;
  total_hours: number;
  total_distance_miles: number | null;
  member_count: number;
  lead_email: string | null;
  lead_name: string | null;
  submission_method: 'manual' | 'auto' | 'api';
  status: 'draft' | 'submitted' | 'acknowledged' | 'rejected';
  notes: string | null;
  submitted_at: string;
  acknowledged_at: string | null;
}

export interface LocationBroadcast {
  id: string;
  crew_id: string;
  telegram_user_id: string;
  telegram_username: string | null;
  latitude: number;
  longitude: number;
  accuracy_meters: number | null;
  speed_mps: number | null;
  heading: number | null;
  recorded_at: string;
}

const API_BASE = '/api/crews';

function getHeaders(): HeadersInit {
  // Use telegram service which has proper fallback logic
  let userId = telegram.getUserId() || '';
  let username = window.Telegram?.WebApp?.initDataUnsafe?.user?.username || '';

  // Fallback for web preview/local testing: generate a stable dev user so crews API isn't 401
  if (!userId) {
    const storedId = localStorage.getItem('dev-user-id');
    const storedName = localStorage.getItem('dev-username');
    if (storedId) {
      userId = storedId;
    } else {
      userId = typeof crypto !== 'undefined' && 'randomUUID' in crypto
        ? `dev-${crypto.randomUUID()}`
        : `dev-${Math.random().toString(36).slice(2, 10)}`;
      localStorage.setItem('dev-user-id', userId);
    }

    if (storedName) {
      username = storedName;
    } else {
      username = 'web-user';
      localStorage.setItem('dev-username', username);
    }
  }

  return {
    'Content-Type': 'application/json',
    'X-Telegram-User-Id': userId,
    'X-Telegram-Username': username,
    'X-Telegram-Init-Data': telegram.getInitData(),
  };
}

export const crewService = {
  /**
   * Get all crews the user is a member of
   */
  async getMyCrews(): Promise<{ crews: Crew[]; total: number }> {
    const response = await fetch(API_BASE, {
      headers: getHeaders()
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to fetch crews');
    }
    return response.json();
  },

  /**
   * Get detailed info about a specific crew
   */
  async getCrewDetails(crewId: string): Promise<{
    crew: Crew;
    members: CrewMember[];
    meetings: Meeting[];
    userRole: string;
  }> {
    const response = await fetch(`${API_BASE}/${crewId}`, {
      headers: getHeaders()
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to fetch crew details');
    }
    return response.json();
  },

  /**
   * Create a new crew
   */
  async createCrew(name: string, description?: string, maxMembers?: number): Promise<{
    success: boolean;
    crewId: string;
    inviteCode: string;
    message: string;
  }> {
    console.log('[crewService] Creating crew:', { name, description, maxMembers });
    const response = await fetch(API_BASE, {
      method: 'POST',
      headers: getHeaders(),
      body: JSON.stringify({ name, description, maxMembers })
    });
    console.log('[crewService] Create response status:', response.status);
    if (!response.ok) {
      const error = await response.json();
      console.error('[crewService] Create crew error:', error);
      throw new Error(error.error || 'Failed to create crew');
    }
    const result = await response.json();
    console.log('[crewService] Crew created:', result);
    return result;
  },

  /**
   * Update crew settings
   */
  async updateCrew(crewId: string, updates: {
    name?: string;
    description?: string;
    maxMembers?: number;
  }): Promise<{ success: boolean }> {
    const response = await fetch(`${API_BASE}/${crewId}`, {
      method: 'PUT',
      headers: getHeaders(),
      body: JSON.stringify(updates)
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to update crew');
    }
    return response.json();
  },

  /**
   * Delete/deactivate a crew
   */
  async deleteCrew(crewId: string): Promise<{ success: boolean }> {
    const response = await fetch(`${API_BASE}/${crewId}`, {
      method: 'DELETE',
      headers: getHeaders()
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to delete crew');
    }
    return response.json();
  },

  /**
   * Join a crew using invite code
   */
  async joinCrew(inviteCode: string): Promise<{
    success: boolean;
    crewId: string;
    crewName: string;
    message: string;
  }> {
    const response = await fetch(`${API_BASE}/join`, {
      method: 'POST',
      headers: getHeaders(),
      body: JSON.stringify({ inviteCode })
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to join crew');
    }
    return response.json();
  },

  /**
   * Leave a crew
   */
  async leaveCrew(crewId: string): Promise<{ success: boolean }> {
    const response = await fetch(`${API_BASE}/members/leave`, {
      method: 'POST',
      headers: getHeaders(),
      body: JSON.stringify({ crewId })
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to leave crew');
    }
    return response.json();
  },

  /**
   * Get crew members
   */
  async getMembers(crewId: string): Promise<{
    members: CrewMember[];
    total: number;
    userRole: string;
  }> {
    const response = await fetch(`${API_BASE}/members?crewId=${crewId}`, {
      headers: getHeaders()
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to fetch members');
    }
    return response.json();
  },

  /**
   * Update member role
   */
  async updateMemberRole(memberId: string, role: 'admin' | 'member'): Promise<{ success: boolean }> {
    const response = await fetch(`${API_BASE}/members/${memberId}`, {
      method: 'PUT',
      headers: getHeaders(),
      body: JSON.stringify({ role })
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to update role');
    }
    return response.json();
  },

  /**
   * Remove/kick a member
   */
  async removeMember(memberId: string): Promise<{ success: boolean }> {
    const response = await fetch(`${API_BASE}/members/${memberId}`, {
      method: 'DELETE',
      headers: getHeaders()
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to remove member');
    }
    return response.json();
  },

  // Meetings API

  /**
   * Get meetings for a crew or all user's crews
   */
  async getMeetings(crewId?: string): Promise<{
    meetings: Meeting[];
    total: number;
  }> {
    const url = crewId ? `${API_BASE}/meetings?crewId=${crewId}` : `${API_BASE}/meetings`;
    const response = await fetch(url, {
      headers: getHeaders()
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to fetch meetings');
    }
    return response.json();
  },

  /**
   * Create a new meeting point
   */
  async createMeeting(data: {
    crewId: string;
    name?: string;
    description?: string;
    latitude: number;
    longitude: number;
    locationName?: string;
    scheduledAt?: string;
    proximityRadiusMeters?: number;
  }): Promise<{
    success: boolean;
    meetingId: string;
    message: string;
  }> {
    const response = await fetch(`${API_BASE}/meetings`, {
      method: 'POST',
      headers: getHeaders(),
      body: JSON.stringify(data)
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to create meeting');
    }
    return response.json();
  },

  /**
   * Update meeting
   */
  async updateMeeting(meetingId: string, updates: {
    name?: string;
    description?: string;
    latitude?: number;
    longitude?: number;
    locationName?: string;
    scheduledAt?: string;
    proximityRadiusMeters?: number;
    status?: 'active' | 'completed' | 'cancelled';
  }): Promise<{ success: boolean }> {
    const response = await fetch(`${API_BASE}/meetings/${meetingId}`, {
      method: 'PUT',
      headers: getHeaders(),
      body: JSON.stringify(updates)
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to update meeting');
    }
    return response.json();
  },

  /**
   * Cancel a meeting
   */
  async cancelMeeting(meetingId: string): Promise<{ success: boolean }> {
    const response = await fetch(`${API_BASE}/meetings/${meetingId}`, {
      method: 'DELETE',
      headers: getHeaders()
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to cancel meeting');
    }
    return response.json();
  },

  // Check-in API

  /**
   * Check in to a meeting
   */
  async checkIn(meetingId: string, latitude: number, longitude: number): Promise<{
    success: boolean;
    checkInId: string;
    distance: number;
    withinProximity: boolean;
    proximityRadius: number;
    message: string;
  }> {
    const response = await fetch(`${API_BASE}/checkin`, {
      method: 'POST',
      headers: getHeaders(),
      body: JSON.stringify({ meetingId, latitude, longitude })
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to check in');
    }
    return response.json();
  },

  /**
   * Update user's location (broadcasts to crew)
   */
  async updateLocation(latitude: number, longitude: number, crewId?: string): Promise<{
    success: boolean;
    updatedCrews: number;
    nearbyMeetings: Array<{
      meetingId: string;
      meetingName: string;
      crewName: string;
      distance: number;
      withinProximity: boolean;
      proximityRadius: number;
    }>;
  }> {
    const response = await fetch(`${API_BASE}/checkin/location`, {
      method: 'POST',
      headers: getHeaders(),
      body: JSON.stringify({ latitude, longitude, crewId })
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to update location');
    }
    return response.json();
  },

  /**
   * Get check-ins for a meeting
   */
  async getCheckIns(meetingId: string): Promise<{
    checkIns: CheckIn[];
    latestByUser: CheckIn[];
    total: number;
  }> {
    const response = await fetch(`${API_BASE}/checkin?meetingId=${meetingId}`, {
      headers: getHeaders()
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to fetch check-ins');
    }
    return response.json();
  },

  /**
   * Copy invite code to clipboard
   */
  async copyInviteCode(inviteCode: string): Promise<boolean> {
    try {
      await navigator.clipboard.writeText(inviteCode);
      return true;
    } catch {
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = inviteCode;
      document.body.appendChild(textArea);
      textArea.select();
      try {
        document.execCommand('copy');
        return true;
      } finally {
        document.body.removeChild(textArea);
      }
    }
  },

  /**
   * Generate shareable crew invite link
   */
  getShareableLink(inviteCode: string): string {
    const baseUrl = typeof window !== 'undefined' ? window.location.origin : '';
    return `${baseUrl}/join?code=${inviteCode}`;
  },

  /**
   * Share invite via Telegram or native share
   */
  async shareInvite(crew: Crew): Promise<boolean> {
    const inviteText = `🚗 Join my crew "${crew.name}" on ZiaWellNav!\n\nUse code: ${crew.invite_code}\n\nOr open: ${this.getShareableLink(crew.invite_code)}`;
    
    // Try Telegram share first if in webapp
    if (window.Telegram?.WebApp?.openTelegramLink) {
      const telegramShareUrl = `https://t.me/share/url?url=${encodeURIComponent(this.getShareableLink(crew.invite_code))}&text=${encodeURIComponent(inviteText)}`;
      window.Telegram.WebApp.openTelegramLink(telegramShareUrl);
      return true;
    }
    
    // Try native share API
    if (navigator.share) {
      try {
        await navigator.share({
          title: `Join ${crew.name}`,
          text: inviteText,
          url: this.getShareableLink(crew.invite_code),
        });
        return true;
      } catch {
        // User cancelled or share failed
      }
    }
    
    // Fallback to clipboard
    return this.copyInviteCode(crew.invite_code);
  },

  /**
   * Update crew lead/timesheet settings
   */
  async updateCrewSettings(crewId: string, settings: {
    leadEmail?: string;
    leadName?: string;
    companyName?: string;
    autoSubmitTimesheet?: boolean;
    timesheetDay?: string;
  }): Promise<{ success: boolean }> {
    const response = await fetch(`${API_BASE}/${crewId}/settings`, {
      method: 'PUT',
      headers: getHeaders(),
      body: JSON.stringify(settings)
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to update settings');
    }
    return response.json();
  },

  /**
   * Submit weekly timesheet to lead
   */
  async submitTimesheet(crewId: string, data: {
    weekStart: string;
    weekEnd: string;
    totalHours: number;
    totalMiles?: number;
    notes?: string;
  }): Promise<{
    success: boolean;
    submissionId: string;
    message: string;
  }> {
    const response = await fetch(`${API_BASE}/${crewId}/timesheet`, {
      method: 'POST',
      headers: getHeaders(),
      body: JSON.stringify(data)
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to submit timesheet');
    }
    return response.json();
  },

  /**
   * Get timesheet submission history
   */
  async getTimesheetHistory(crewId: string): Promise<{
    submissions: TimesheetSubmission[];
    total: number;
  }> {
    const response = await fetch(`${API_BASE}/${crewId}/timesheet`, {
      headers: getHeaders()
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to fetch timesheet history');
    }
    return response.json();
  },

  /**
   * Broadcast current location to crew
   */
  async broadcastLocation(crewId: string, location: {
    latitude: number;
    longitude: number;
    accuracy?: number;
    speed?: number;
    heading?: number;
  }): Promise<{ success: boolean }> {
    const response = await fetch(`${API_BASE}/${crewId}/location`, {
      method: 'POST',
      headers: getHeaders(),
      body: JSON.stringify(location)
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to broadcast location');
    }
    return response.json();
  },

  /**
   * Get crew members' live locations
   */
  async getCrewLocations(crewId: string): Promise<{
    locations: LocationBroadcast[];
    lastUpdated: string;
  }> {
    const response = await fetch(`${API_BASE}/${crewId}/locations`, {
      headers: getHeaders()
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to fetch crew locations');
    }
    return response.json();
  },

  /**
   * Generate QR code data URL for invite
   */
  async generateInviteQR(inviteCode: string): Promise<string> {
    // Simple QR code generation using a free API
    const url = this.getShareableLink(inviteCode);
    return `https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(url)}`;
  }
};

export default crewService;
